import { CTAButton } from "@/components/atoms/PromoBanners/RootBanner";
import { HeroSection } from "@/containers/HeroSection";
import Toolbar from "@/containers/Toolbar";
import { InfoIcon } from "@/designSystemIcons";
import { IosImportPopup } from "@/features/MyClub/IosImportPopup";
import { MyClubBar } from "@/features/MyClub/MyClubBar";
import { Recommendations } from "@/features/MyClub/Recommendations/Recommendations";
import { Spaces } from "@/features/MyClub/Spaces/Spaces";
import { StartImport } from "@/features/MyClub/StartImport";
import { ChemistryProfiles } from "@/functions/calculateChemistry";
import {
  fetchChemistryProfiles,
  loadedContentSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import {
  clubStatsSelector,
  fetchClubStats,
  myClubSelector,
} from "@/redux/myClub/myClubSlice";
import { loadChemistryProfiles as loadChemistryProfilesDefaultSquadBuilder } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
} from "@/redux/user/userSlice";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { isMobileSafari } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { MyClubPlayersNavigationContext } from "./Players/MyClubPlayers";

interface MyClubHomeProps {
  importFinished: boolean;
  handleUpdateClubRecsFinished: () => void;
}
const MyClubHome = ({
  importFinished,
  handleUpdateClubRecsFinished,
}: MyClubHomeProps) => {
  return (
    <div className="flex flex-col gap-y-2 mobile:gap-y-4">
      <Recommendations
        importFinished={importFinished}
        handleUpdateClubRecsFinished={handleUpdateClubRecsFinished}
      />
      <Spaces />
    </div>
  );
};

interface SubscriptionHookProps {
  handleUpgradeNow: () => void;
}

const SubscriptionHook = ({ handleUpgradeNow }: SubscriptionHookProps) => {
  return (
    <section className="flex flex-col mx-auto text-center mobile:w-156 my-2 touch-device:mt-16 gap-y-4 items-center max-mobile:px-8 min-h-38 justify-center">
      <p className="text-sm mobile:text-lg text-dark-secondary">
        Get daily recommendations for the best upgrades to your squad with{" "}
        <span className="text-dark-primary font-bold italic">
          EasySBC Ultimate
        </span>
      </p>
      <CTAButton onClick={handleUpgradeNow} label="Upgrade Now" />
    </section>
  );
};

export const MyClub = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { chemistryProfilesStatus } = useSelector(loadedContentSelector);
  const { fetchClubStatsStatus } = useSelector(myClubSelector);
  const shouldShowFreeVersion = !useSelector(canUseClubImportSelector);
  const isRootRoute = location.pathname === "/my-club";
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const navigate = useNavigate();
  const fromExtensioniOSApp = !!localStorage.getItem("FROM_EXTENSION_APP");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [importFinished, setImportFinished] = useState(false);
  const { user } = useSelector(getUserSelector);
  const canUseClubImportAndHasntImportedYet = useMemo(() => {
    return (
      (user.subscription === "diamond" || user.subscription === "gold") &&
      !user.lastImportedAt
    );
  }, [user]);

  useEffect(() => {
    const state = location.state as { importFinished?: boolean } | undefined;
    if (state?.importFinished) {
      dispatch(fetchClubStats());
      setImportFinished(true);
    }
  }, [location.state]);

  const currentSection =
    "My " +
    pathSegments[pathSegments.length - 1]
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  useEffect(() => {
    if (fetchClubStatsStatus === "idle" && !shouldShowFreeVersion) {
      dispatch(fetchClubStats());
    }
    if (chemistryProfilesStatus === "idle") {
      dispatch(fetchChemistryProfiles()).then((res) => {
        dispatch(
          loadChemistryProfilesDefaultSquadBuilder(
            res.payload as ChemistryProfiles,
          ),
        );
      });
    }
  }, [fetchClubStatsStatus, chemistryProfilesStatus, shouldShowFreeVersion]);

  const clubStats = useSelector(clubStatsSelector);

  return (
    <>
      <section
        className="flex flex-col items-center mobile:h-57 relative"
        style={{
          background: "linear-gradient(192deg, #1C2A37 4.18%, #0F1821 125.88%)",
        }}
      >
        <HeroSection
          title={
            isRootRoute
              ? "EA FC 25 My Club -  Smart Management for Your Club"
              : "EA FC 25 " +
                currentSection +
                " -  Smart Management for Your Club"
          }
          {...(isRootRoute && {
            content:
              "See the best upgrades for your squad. Track your club’s value. Find players you can sell for easy profit.",
          })}
          titleBackgroundColor="bg-white/5"
          {...(!isRootRoute && { navigateBack: () => navigate("/my-club") })}
          children={
            shouldShowFreeVersion ? (
              <SubscriptionHook
                handleUpgradeNow={() => navigate("/subscription")}
              />
            ) : (
              <section className="mobile:mt-8 touch-device:mt-24 w-full mobile:px-4 touch-device:px-16 max-w-content mx-auto">
                <MyClubBar
                  clubName={clubStats.clubName}
                  clubValue={clubStats.clubValue ?? 0}
                  coins={clubStats.coins ?? 0}
                  SBCFodder={clubStats.sbcFodder ?? 0}
                  potentialCoins={clubStats.potentialCoins ?? 0}
                  transferList={clubStats.transferList ?? 0}
                  clubRank={clubStats.rank ?? 0}
                  clubImageUrl={
                    clubStats.clubBadgeId
                      ? import.meta.env.VITE_APP_ASSETS_BASE_URL +
                        `/fc25/clubs/${clubStats.clubBadgeId}.png`
                      : undefined
                  }
                />
              </section>
            )
          }
        />
      </section>
      <Toolbar>
        <section className="flex flex-row gap-2 items-center justify-between w-full">
          {shouldShowFreeVersion && (
            <div className="flex flex-row gap-x-2 items-center mobile:bg-new-gray-850 mobile:px-3 py-1 rounded-md">
              <InfoIcon
                innerFill="white"
                outerFill="var(--color-error)"
                width="16"
                height="16"
              />
              <div className="text-dark-secondary text-xxs small-mobile:text-xs w-80 small-mobile:w-90">
                You are seeing the newest recommendations for another user.{" "}
                <br />
                With Ultimate, you can see it for your own club.
              </div>
            </div>
          )}
          <div className="flex-row gap-2 items-center justify-end w-full ml-auto flex">
            {currentSection === "My Players" && (
              <MyClubPlayersNavigationContext />
            )}
            <p className="text-dark-tertiary text-xs">
              We're having issues with club import. Please visit the{" "}
              <a
                href="https://beta.easysbc.io"
                className="text-dark-secondary underline"
              >
                beta site
              </a>{" "}
              to import your club.
            </p>
            <button
              className={clsx(
                "relative mobile:block",
                !isMobileSafari && "hidden",
              )}
            >
              <StartImport
                disabled={shouldShowFreeVersion}
                handleImportFinished={() => {
                  setImportFinished(true);
                }}
                shouldntImport={
                  isMobileSafari ||
                  fromExtensioniOSApp ||
                  canUseClubImportAndHasntImportedYet
                }
                handleClickWhenShouldntImport={() => {
                  if (fromExtensioniOSApp) {
                    navigate("/import-mobile");
                  } else if (canUseClubImportAndHasntImportedYet) {
                    navigate("/import");
                  } else {
                    setIsPopupOpen(true);
                  }
                }}
              />
            </button>
          </div>
        </section>
      </Toolbar>
      <div className="bg-panel-base h-full min-h-screen">
        <main className="max-w-content mx-auto py-4 touch-device:py-8 relative mobile:px-4 large-desktop:px-16">
          {isRootRoute ? (
            <MyClubHome
              handleUpdateClubRecsFinished={() => {
                setImportFinished(false);
              }}
              importFinished={importFinished}
            />
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {isPopupOpen && <IosImportPopup onClose={() => setIsPopupOpen(false)} />}
    </>
  );
};
